// Approve progress for the two result pages. The server answers /approve at ENQUEUE time, so "done" is
// learned by polling the claim's status (never from the HTTP call itself):
//
//   queued | running | reconciling => processing: flow inactivity is PAUSED ("התמונה בעיבוד"), poll every 2 s
//                                     (network errors back off to 10 s)
//   done                           => terminal "done" (the pause ends and a full 90 s restarts)
//   failed                         => terminal "failed" (definite: the heads are available again)
//   blocked, or NO ok reply (approve or status, including a fetch that never settles) for longer than
//   QUEUE_WAIT_MAX_S + RUN_MAX_S (independent wall-clock watchdog)
//                                  => "pending" ("התמונה עדיין בטיפול"): the timer resumes, polling slows down
//   410 (claim_unknown)            => terminal "lost"
//
// UI state never affects server ownership. Every poll continuation re-checks the captured event (scope
// object identity) and the mounted flag; unmount stops the loop.
import { useCallback, useEffect, useRef, useState } from "react";
import { getEventScope, isCurrentScope } from "../../../eventIdentity";
import { getFaceClaimEvent } from "./faceClaimEvent";
import { fetchApproveStatus } from "./faceClaimClient";
import { pauseFlowInactivity } from "./useFlowInactivity";

export const POLL_MS = 2000;
export const POLL_BACKOFF_MAX_MS = 10000;
export const PENDING_POLL_MS = 10000;
export const UNREACHABLE_PENDING_MS = (180 + 50) * 1000; // QUEUE_WAIT_MAX_S + RUN_MAX_S

export const MSG_PROCESSING = "התמונה בעיבוד";
export const MSG_STILL_PENDING = "התמונה עדיין בטיפול";
export const MSG_FAILED = "העיבוד נכשל, נא לבחור שוב";

const PROCESSING_CODES = ["queued", "running", "reconciling"];

// phase: idle | processing | pending | done | failed | lost
export const useApproveStatus = () => {
  const [phase, setPhase] = useState("idle");
  const mountedRef = useRef(true);
  // The event visit this instance belongs to (captured at mount, like the pages). A still-mounted reused
  // instance must never act for a different visit (A -> B, A -> B -> A): scope OBJECT identity.
  const scopeRef = useRef(getEventScope());
  const runRef = useRef(0); // each hold()/start() supersedes the previous loop and its watchdog
  const releasePauseRef = useRef(null);
  const timerRef = useRef(null);
  const watchdogRef = useRef(null);

  const endPause = useCallback(() => {
    if (releasePauseRef.current) {
      releasePauseRef.current();
      releasePauseRef.current = null;
    }
  }, []);

  const beginPause = useCallback(() => {
    if (!releasePauseRef.current) releasePauseRef.current = pauseFlowInactivity();
  }, []);

  const clearWatchdog = useCallback(() => {
    if (watchdogRef.current) clearTimeout(watchdogRef.current);
    watchdogRef.current = null;
  }, []);

  // Independent wall-clock watchdog: if nothing proves progress (an ok status reply, or the very start)
  // for UNREACHABLE_PENDING_MS, show "still pending" and let the inactivity timer resume, even when the
  // approve / status fetch never settles. It only changes UI/pause state; server ownership is untouched,
  // and a later reply of THIS run still lands normally. Superseded / unmounted / other-visit runs are inert.
  const armWatchdog = useCallback(
    (run) => {
      clearWatchdog();
      watchdogRef.current = setTimeout(() => {
        watchdogRef.current = null;
        if (!mountedRef.current || runRef.current !== run) return;
        if (!isCurrentScope(scopeRef.current)) return;
        endPause();
        setPhase("pending");
      }, UNREACHABLE_PENDING_MS);
    },
    [clearWatchdog, endPause]
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      runRef.current += 1;
      if (timerRef.current) clearTimeout(timerRef.current);
      clearWatchdog();
      endPause();
    };
  }, [endPause, clearWatchdog]);

  // The instance's own visit is still the active one.
  const activeVisit = useCallback(
    () => mountedRef.current && Boolean(scopeRef.current) && isCurrentScope(scopeRef.current),
    []
  );

  // Called by the page BEFORE it sends /approve (the request itself is processing time too). Refused (false)
  // for an inactive / superseded caller: no pause, no state.
  const hold = useCallback(() => {
    if (!activeVisit()) return false;
    runRef.current += 1;
    if (timerRef.current) clearTimeout(timerRef.current);
    beginPause();
    setPhase("processing");
    armWatchdog(runRef.current);
    return true;
  }, [activeVisit, beginPause, armWatchdog]);

  // The approve was refused/failed before any claim exists: back to idle and resume the timer.
  const abandon = useCallback(() => {
    runRef.current += 1;
    if (timerRef.current) clearTimeout(timerRef.current);
    clearWatchdog();
    endPause();
    if (mountedRef.current) setPhase("idle");
  }, [endPause, clearWatchdog]);

  // Poll the claim's status. onTerminal(phase) fires once for done | failed | lost. `scope` (optional) is the
  // caller's captured visit; start() returns false and does NOTHING (no pause, no request) unless this
  // instance, the caller and the active event are all the same visit.
  const start = useCallback(
    ({ requestId, claimToken, onTerminal, scope }) => {
      const event = getFaceClaimEvent();
      if (!event || !activeVisit() || event.scope !== scopeRef.current) return false;
      if (scope && scope !== event.scope) return false;
      runRef.current += 1;
      const run = runRef.current;
      if (timerRef.current) clearTimeout(timerRef.current); // supersede the previous loop's pending poll
      const live = () =>
        mountedRef.current && runRef.current === run && event.isCurrent();
      beginPause();
      setPhase("processing");
      armWatchdog(run);
      let delay = POLL_MS;

      const finish = (terminal) => {
        clearWatchdog();
        endPause();
        setPhase(terminal);
        if (onTerminal) onTerminal(terminal);
      };

      const poll = async () => {
        if (!live()) return;
        const result = await fetchApproveStatus(event, { requestId, claimToken });
        if (!live()) return;
        if (result.status === 410) {
          finish("lost");
          return;
        }
        if (!result.ok) {
          // network / server trouble (a status fetch is bounded by STATUS_POLL_TIMEOUT_MS): keep polling with
          // backoff; the watchdog above tells the guest it is still being handled after UNREACHABLE_PENDING_MS.
          delay = Math.min(POLL_BACKOFF_MAX_MS, delay * 2);
        } else {
          armWatchdog(run); // an ok reply proves the server is reachable: the window restarts
          if (result.code === "done") return finish("done");
          if (result.code === "failed") return finish("failed");
          if (result.code === "blocked") {
            endPause();
            setPhase("pending");
            delay = PENDING_POLL_MS;
          } else if (PROCESSING_CODES.includes(result.code)) {
            delay = POLL_MS;
            beginPause();
            setPhase("processing");
          }
        }
        timerRef.current = setTimeout(poll, delay);
      };
      poll();
      return true;
    },
    [activeVisit, beginPause, endPause, armWatchdog, clearWatchdog]
  );

  return { phase, hold, start, abandon };
};
